import { FC } from 'react';
import * as ScrollArea from '@radix-ui/react-scroll-area';
import { X, History, Ban, CheckCircle2, Clock, XCircle } from 'lucide-react';

import {
  type CourseHearing,
  type CourseHearingAttempt,
  type HearingSubmissionSummary,
} from '@teachur/http-client';

import { EmptyState } from '@new/common/ui/EmptyState/EmptyState';
import { SubmissionDetailContent } from '@new/common/ui/SubmissionDetail/SubmissionDetailContent';
import { toHearingSubmissionDetail } from '@new/common/ui/SubmissionDetail/submissionDetail.mappers';
import { formatShortDate, timestampToDate } from '@new/common/lib/dateTime';

interface CourseHearingSubmissionsHistorySidePanelProps {
  call: CourseHearing | null;
  onClose: () => void;
}

interface HearingHistorySubmission {
  submission: HearingSubmissionSummary;
  isCancelled: boolean;
  // The effective meeting date: when the call was actually held
  // (videoCallEndedAt) else the scheduled startTime. Held-else-scheduled,
  // mirroring the list's "Meeting time". `null` for legacy previousReviews
  // that have no attempt (their submission.createdAt is a backfill placeholder,
  // so no meaningful date is shown). Also drives ordering.
  meetingDate: string | null;
  sortKey: number;
}

// Effective meeting time for an attempt: the real held time if the call
// happened, else the scheduled slot. Returns { label, sortKey } where sortKey
// is epoch ms (0 when unknown) so ordering matches the displayed date.
const getAttemptMeetingDate = (
  attempt: CourseHearingAttempt
): { label: string | null; sortKey: number } => {
  if (attempt.videoCallEndedAt) {
    const held = new Date(attempt.videoCallEndedAt);
    // A present-but-unparseable endedAt must not win over a valid startTime:
    // only use the held time when it is a valid date, else fall through to the
    // scheduled slot below.
    if (!Number.isNaN(held.getTime())) {
      return { label: formatShortDate(held), sortKey: held.getTime() };
    }
  }

  const scheduled = timestampToDate(attempt.startTime);
  if (Number.isNaN(scheduled.getTime())) {
    return { label: null, sortKey: 0 };
  }
  return { label: formatShortDate(scheduled), sortKey: scheduled.getTime() };
};

const getStatusBadge = (submission: HearingSubmissionSummary, isCancelled: boolean) => {
  if (isCancelled) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#FEE]">
        <Ban size={16} className="text-[#E53E3E]" />
        <span className="[font-size:var(--fs-l5)]! font-semibold text-[#E53E3E]">Cancelled</span>
      </div>
    );
  }

  if (submission.status === 'approved') {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#E6F7EF]">
        <CheckCircle2 size={16} className="text-[#47B881]" />
        <span className="[font-size:var(--fs-l5)]! font-semibold text-[#47B881]">Approved</span>
      </div>
    );
  }

  if (submission.status === 'notYet') {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#FEE]">
        <XCircle size={16} className="text-[#E53E3E]" />
        <span className="[font-size:var(--fs-l5)]! font-semibold text-[#E53E3E]">Not Yet</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#FFF9EE]">
      <Clock size={16} className="text-[#FE9B0E]" />
      <span className="[font-size:var(--fs-l5)]! font-semibold text-[#FE9B0E]">Pending Review</span>
    </div>
  );
};

export const CourseHearingSubmissionsHistorySidePanel: FC<
  CourseHearingSubmissionsHistorySidePanelProps
> = ({ call, onClose }) => {
  const hearing = call;

  const attemptSubmissions: HearingHistorySubmission[] = (hearing?.attempts ?? [])
    .filter((attempt) => !!attempt.hearingSubmission)
    .filter((attempt) => {
      const isCancelled = attempt.status === 'rejected';
      const isOpen =
        attempt.hearingSubmission!.status === 'pending' ||
        attempt.hearingSubmission!.status === 'inReview';
      return isCancelled || !isOpen;
    })
    .map((attempt) => {
      const { label, sortKey } = getAttemptMeetingDate(attempt);
      return {
        submission: attempt.hearingSubmission!,
        isCancelled: attempt.status === 'rejected',
        meetingDate: label,
        sortKey,
      };
    });

  // Legacy reviews have no attempt, so no meeting date to show or sort by.
  const previousReviews: HearingHistorySubmission[] = (hearing?.previousReviews ?? []).map(
    (submission) => ({
      submission,
      isCancelled: false,
      meetingDate: null,
      sortKey: 0,
    })
  );

  const submissions = [...attemptSubmissions, ...previousReviews].sort(
    (a, b) => b.sortKey - a.sortKey
  );

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-(--neutral-white-NW800) mr-[2%]">
        <div className="flex items-center gap-2">
          <History className="w-5 h-5 text-(--neutral-black-NB500)" />

          <h3 className="[font-size:var(--fs-h6)]! font-bold text-(--neutral-black-NB1000)! mb-0!">
            Submission History
          </h3>
        </div>

        <button
          onClick={onClose}
          className="rounded-md text-(--neutral-black-NB500) hover:text-(--neutral-black-NB900) cursor-pointer"
          aria-label="Close submission history"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <ScrollArea.Root className="flex-1 min-h-0 overflow-hidden">
        <ScrollArea.Viewport className="w-full h-full p-4 mr-[2%]">
          {submissions.length === 0 ? (
            <EmptyState
              message="No previous submissions"
              description="This is the first course hearing for this student."
            />
          ) : (
            <div className="flex flex-col gap-4">
              {submissions.map(({ submission, isCancelled, meetingDate }) => {
                const statusBadge = getStatusBadge(submission, isCancelled);

                return (
                  <div
                    key={submission.id}
                    className="rounded-[12px] border border-(--neutral-white-NW800) p-4 bg-(--neutral-white-NW1000)"
                  >
                    <SubmissionDetailContent
                      data={toHearingSubmissionDetail(submission, statusBadge, meetingDate)}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea.Viewport>

        <ScrollArea.Scrollbar
          orientation="vertical"
          className="flex w-2.5 bg-transparent p-0.5 hover:bg-gray-100 transition-colors mr-1"
        >
          <ScrollArea.Thumb className="flex-1 bg-gray-300 rounded-full hover:bg-gray-400 transition-colors" />
        </ScrollArea.Scrollbar>

        <ScrollArea.Corner className="bg-gray-100" />
      </ScrollArea.Root>
    </div>
  );
};
